(() => {
  'use strict';

  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'NOK';
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2
    }).format(value);
  };

  const setCartCount = (count) => {
    document.querySelectorAll('[data-pv-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    });
    document.querySelectorAll('[data-pv-cart-link]').forEach((link) => {
      link.setAttribute('aria-label', `Handlekurv, ${count} varer`);
    });
  };

  const closeDropdowns = (except) => {
    document.querySelectorAll('[data-pv-dropdown]').forEach((dropdown) => {
      if (dropdown === except) return;
      dropdown.dataset.pvOpen = 'false';
      const toggle = dropdown.querySelector('[data-pv-dropdown-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  const initCartDrawer = (header, closeMobileMenu) => {
    const trigger = header.querySelector('[data-pv-cart-drawer-trigger]');
    const drawer = header.querySelector('[data-pv-cart-drawer]');
    const overlay = header.querySelector('[data-pv-cart-drawer-overlay]');
    const closeButton = header.querySelector('[data-pv-cart-drawer-close]');
    if (!trigger || !drawer || !overlay || drawer.dataset.pvReady === 'true') return;
    drawer.dataset.pvReady = 'true';
    const loading = drawer.querySelector('[data-pv-cart-loading]');
    const error = drawer.querySelector('[data-pv-cart-error]');
    const empty = drawer.querySelector('[data-pv-cart-empty]');
    const items = drawer.querySelector('[data-pv-cart-items]');
    const footer = drawer.querySelector('[data-pv-cart-footer]');
    const shipping = drawer.querySelector('[data-pv-cart-shipping]');
    const shippingPrice = drawer.querySelector('[data-pv-cart-shipping-price]');
    const delivered = drawer.querySelector('[data-pv-cart-delivered]');
    const subtotal = drawer.querySelector('[data-pv-cart-subtotal]');
    const minusIcon = drawer.querySelector('[data-pv-cart-icon-minus]');
    const plusIcon = drawer.querySelector('[data-pv-cart-icon-plus]');
    let open = false;
    let returnFocus = null;

    const renderCart = (cart) => {
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      items.replaceChildren();
      setCartCount(Number(cart?.item_count || 0));
      cartItems.forEach((item) => {
        const line = document.createElement('li');
        line.className = 'pv-cart-line';
        const imageUrl = item.featured_image?.url || item.image;
        if (imageUrl) {
          const imageLink = document.createElement('a');
          imageLink.className = 'pv-cart-line__image-link';
          imageLink.href = item.url || `${rootPath()}cart`;
          const image = document.createElement('img');
          image.className = 'pv-cart-line__image';
          image.src = imageUrl;
          image.alt = '';
          image.width = 80;
          image.height = 80;
          image.loading = 'lazy';
          imageLink.appendChild(image);
          line.appendChild(imageLink);
        } else {
          const spacer = document.createElement('span');
          spacer.setAttribute('aria-hidden', 'true');
          line.appendChild(spacer);
        }
        const content = document.createElement('div');
        content.className = 'pv-cart-line__content';
        const title = document.createElement('a');
        title.className = 'pv-cart-line__title';
        title.href = item.url || `${rootPath()}cart`;
        title.textContent = item.product_title || item.title || 'Vare';
        content.appendChild(title);
        if (item.variant_title && item.variant_title !== 'Default Title') {
          const variant = document.createElement('p');
          variant.className = 'pv-cart-line__variant';
          variant.textContent = item.variant_title;
          content.appendChild(variant);
        }
        const meta = document.createElement('div');
        meta.className = 'pv-cart-line__meta';
        const quantity = document.createElement('div');
        quantity.className = 'pv-cart-line__quantity';
        const decrement = document.createElement('button');
        decrement.type = 'button';
        if (minusIcon) decrement.appendChild(minusIcon.content.cloneNode(true));
        else decrement.textContent = 'Mindre';
        decrement.dataset.pvCartChange = 'decrement';
        decrement.dataset.pvCartKey = item.key;
        decrement.dataset.pvCartQuantity = String(Number(item.quantity || 1));
        decrement.setAttribute('aria-label', `Reduser antall ${item.product_title || item.title || 'vare'} med én`);
        const quantityInput = document.createElement('input');
        quantityInput.type = 'number';
        quantityInput.min = '0';
        quantityInput.inputMode = 'numeric';
        quantityInput.readOnly = true;
        quantityInput.value = String(Number(item.quantity || 1));
        quantityInput.setAttribute('aria-label', `Antall ${item.product_title || item.title || 'vare'}`);
        const increment = document.createElement('button');
        increment.type = 'button';
        if (plusIcon) increment.appendChild(plusIcon.content.cloneNode(true));
        else increment.textContent = 'Mer';
        increment.dataset.pvCartChange = 'increment';
        increment.dataset.pvCartKey = item.key;
        increment.dataset.pvCartQuantity = String(Number(item.quantity || 1));
        increment.setAttribute('aria-label', `Øk antall ${item.product_title || item.title || 'vare'} med én`);
        quantity.append(decrement, quantityInput, increment);
        const price = document.createElement('strong');
        price.className = 'pv-cart-line__price';
        price.textContent = formatMoney(item.final_line_price ?? item.line_price ?? 0);
        meta.append(quantity, price);
        content.appendChild(meta);
        line.appendChild(content);
        items.appendChild(line);
      });

      const hasItems = cartItems.length > 0;
      loading.hidden = true;
      error.hidden = true;
      drawer.setAttribute('aria-busy', 'false');
      empty.hidden = hasItems;
      footer.hidden = !hasItems;
      if (hasItems) {
        const total = Number(cart.total_price || 0);
        const freeShippingAt = 80000;
        const standardShipping = total >= freeShippingAt ? 0 : 4900;
        subtotal.textContent = formatMoney(total);
        shipping.textContent = total >= freeShippingAt ? 'Gratis PostNord-frakt' : 'PostNord standard';
        shippingPrice.textContent = formatMoney(standardShipping);
        delivered.textContent = formatMoney(total + standardShipping);
      }
    };

    const changeCart = async (key, quantity) => {
      if (!key || !Number.isFinite(quantity) || quantity < 0) return;
      drawer.setAttribute('aria-busy', 'true');
      items.querySelectorAll('[data-pv-cart-change]').forEach((button) => { button.disabled = true; });
      error.hidden = true;
      try {
        const response = await fetch(`${rootPath()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Cart change failed');
        const cart = await response.json();
        renderCart(cart);
        document.dispatchEvent(new CustomEvent('peveo:cart-updated', { detail: { cart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart }, bubbles: true }));
      } catch (_cartChangeError) {
        error.hidden = false;
        drawer.setAttribute('aria-busy', 'false');
        items.querySelectorAll('[data-pv-cart-change]').forEach((button) => { button.disabled = false; });
      }
    };

    const loadCart = async () => {
      drawer.setAttribute('aria-busy', 'true');
      loading.hidden = false;
      error.hidden = true;
      empty.hidden = true;
      footer.hidden = true;
      items.replaceChildren();
      try {
        const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart fetch failed');
        renderCart(await response.json());
      } catch (_cartError) {
        loading.hidden = true;
        error.hidden = false;
        drawer.setAttribute('aria-busy', 'false');
      }
    };

    const setDrawer = (nextOpen, cart) => {
      open = nextOpen;
      if (open) {
        returnFocus = document.activeElement;
        closeMobileMenu(false);
        closeDropdowns();
        drawer.hidden = false;
        overlay.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('pv-cart-open');
        document.querySelector('#MainContent, main')?.setAttribute('inert', '');
        if (cart) renderCart(cart);
        else void loadCart();
        window.requestAnimationFrame(() => closeButton?.focus());
      } else {
        drawer.hidden = true;
        overlay.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('pv-cart-open');
        document.querySelector('#MainContent, main')?.removeAttribute('inert');
        if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
      }
    };

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      setDrawer(true);
    });
    overlay.addEventListener('click', () => setDrawer(false));
    closeButton?.addEventListener('click', () => setDrawer(false));
    items.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pv-cart-change]');
      if (!button) return;
      const current = Number(button.dataset.pvCartQuantity || 0);
      const next = button.dataset.pvCartChange === 'increment' ? current + 1 : Math.max(0, current - 1);
      void changeCart(button.dataset.pvCartKey, next);
    });
    document.addEventListener('keydown', (event) => {
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setDrawer(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    document.addEventListener('peveo:cart-updated', (event) => {
      if (event.detail?.cart) {
        setCartCount(Number(event.detail.cart.item_count || 0));
        if (open) renderCart(event.detail.cart);
      }
    });
    document.addEventListener('peveo:cart-open', (event) => setDrawer(true, event.detail?.cart));
  };

  const initHeader = (header) => {
    if (header.dataset.pvReady === 'true') return;
    header.dataset.pvReady = 'true';
    const toggle = header.querySelector('[data-pv-menu-toggle]');
    const drawer = header.querySelector('[data-pv-mobile-menu]');
    const overlay = header.querySelector('[data-pv-menu-overlay]');
    const openIcon = header.querySelector('[data-pv-menu-open-icon]');
    const closeIcon = header.querySelector('[data-pv-menu-close-icon]');
    let lastFocused = null;

    const setMenu = (open) => {
      if (!toggle || !drawer || !overlay) return;
      const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Lukk meny' : 'Åpne meny');
      drawer.hidden = !open;
      overlay.hidden = !open;
      if (openIcon) openIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;
      document.body.classList.toggle('pv-menu-open', open);
      const main = document.querySelector('#MainContent, main');
      if (main) {
        if (open) main.setAttribute('inert', '');
        else if (!document.body.classList.contains('pv-cart-open')) main.removeAttribute('inert');
      }
      if (open) {
        lastFocused = document.activeElement;
        window.requestAnimationFrame(() => drawer.querySelector('a, button')?.focus());
      } else if (wasOpen && lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };

    initCartDrawer(header, setMenu);

    toggle?.addEventListener('click', () => setMenu(toggle.getAttribute('aria-expanded') !== 'true'));
    overlay?.addEventListener('click', () => setMenu(false));
    header.querySelectorAll('[data-pv-close-menu]').forEach((element) => element.addEventListener('click', () => setMenu(false)));

    header.querySelectorAll('[data-pv-dropdown]').forEach((dropdown) => {
      const dropdownToggle = dropdown.querySelector('[data-pv-dropdown-toggle]');
      let timer;
      const setDropdown = (open) => {
        window.clearTimeout(timer);
        closeDropdowns(open ? dropdown : null);
        dropdown.dataset.pvOpen = String(open);
        dropdownToggle?.setAttribute('aria-expanded', String(open));
      };
      dropdownToggle?.addEventListener('click', () => setDropdown(dropdown.dataset.pvOpen !== 'true'));
      dropdown.addEventListener('mouseenter', () => setDropdown(true));
      dropdown.addEventListener('mouseleave', () => { timer = window.setTimeout(() => setDropdown(false), 150); });
      dropdown.addEventListener('focusout', (event) => {
        if (!dropdown.contains(event.relatedTarget)) setDropdown(false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeDropdowns();
      setMenu(false);
    });
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (event) => {
      if (event.matches) setMenu(false);
    });
  };

  const initRail = (home) => {
    const rail = home.querySelector('[data-pv-product-rail]');
    if (!rail || rail.dataset.pvReady === 'true') return;
    rail.dataset.pvReady = 'true';
    const amount = () => Math.min(360, rail.clientWidth * 0.86);
    home.querySelector('[data-pv-rail-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -amount(), behavior: 'smooth' }));
    home.querySelector('[data-pv-rail-next]')?.addEventListener('click', () => rail.scrollBy({ left: amount(), behavior: 'smooth' }));
  };

  const initSticky = (home) => {
    const sticky = home.querySelector('[data-pv-mobile-sticky]');
    if (!sticky || sticky.dataset.pvReady === 'true') return;
    sticky.dataset.pvReady = 'true';
    let dismissed = false;
    const update = () => {
      if (dismissed || window.matchMedia('(min-width: 1024px)').matches) {
        sticky.hidden = true;
        return;
      }
      const blocked = Array.from(home.querySelectorAll('[data-pv-sales-suppress]')).some((element) => {
        const rect = element.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
      sticky.hidden = !(window.scrollY > window.innerHeight * 0.7 && !blocked);
    };
    sticky.querySelector('[data-pv-sticky-dismiss]')?.addEventListener('click', () => {
      dismissed = true;
      sticky.hidden = true;
    });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  };

  const readItems = (form) => {
    const indexed = Array.from(form.querySelectorAll('input[name^="items["][name$="[id]"]'));
    if (indexed.length) {
      return indexed.map((input) => {
        const match = input.name.match(/items\[(\d+)]/);
        const quantity = form.querySelector(`input[name="items[${match?.[1]}][quantity]"]`);
        return { id: Number(input.value), quantity: Number(quantity?.value || 1) };
      });
    }
    const id = form.querySelector('input[name="id"]');
    const quantity = form.querySelector('input[name="quantity"]');
    return id ? [{ id: Number(id.value), quantity: Number(quantity?.value || 1) }] : [];
  };

  const initCartForm = (form) => {
    if (form.dataset.pvReady === 'true') return;
    form.dataset.pvReady = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const status = form.querySelector('[data-pv-form-status]');
      const items = readItems(form);
      if (!items.length || !items.every((item) => Number.isFinite(item.id))) {
        form.submit();
        return;
      }
      const originalLabel = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'Legger til…';
      }
      if (status) status.hidden = true;
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Cart add failed');
        let cart = null;
        try {
          const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) cart = await cartResponse.json();
        } catch (_cartRefreshError) {
          cart = null;
        }
        if (cart) setCartCount(Number(cart.item_count || 0));
        if (status) {
          status.textContent = items.length > 1 ? 'Startpakken er lagt i handlekurven.' : 'Varen er lagt i handlekurven.';
          status.dataset.state = 'success';
          status.hidden = false;
        }
        if (cart) document.dispatchEvent(new CustomEvent('peveo:cart-updated', { detail: { cart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('peveo:cart-open', { detail: { cart }, bubbles: true }));
      } catch (_error) {
        if (status) {
          status.textContent = 'Kunne ikke legge til varen. Prøv igjen eller åpne produktsiden.';
          status.dataset.state = 'error';
          status.hidden = false;
        }
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }
    });
  };

  const initPrivacyButtons = () => {
    document.querySelectorAll('[data-pv-privacy-settings]:not([data-pv-ready="true"])').forEach((button) => {
      button.dataset.pvReady = 'true';
      button.addEventListener('click', () => {
        const nativeButton = document.querySelector('#shopify-pc__banner__btn-manage-prefs, [data-shopify-policy-banner-manage], .shopify-pc__banner__btn-manage-prefs');
        if (nativeButton) nativeButton.click();
        else document.dispatchEvent(new CustomEvent('peveo:privacy-settings-requested', { bubbles: true }));
      });
    });
  };

  const initEditorialAnchors = (scope) => {
    scope.querySelectorAll('[data-peveo-page-content] h2:not([id])').forEach((heading) => {
      const normalized = heading.textContent.trim().toLocaleLowerCase('nb-NO');
      if (normalized === 'smak betyr noe') heading.id = 'smaksutvikling';
    });
    if (!window.location.hash) return;
    const target = scope.querySelector(window.location.hash);
    if (target) window.requestAnimationFrame(() => target.scrollIntoView());
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pv-header]').forEach(initHeader);
    scope.querySelectorAll('[data-pv-home]').forEach((home) => {
      initRail(home);
      initSticky(home);
    });
    scope.querySelectorAll('[data-pv-cart-form]').forEach(initCartForm);
    initPrivacyButtons();
    initEditorialAnchors(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
